import { Check, Shield, KeyRound } from "lucide-react";
import { cn } from "@/lib/utils";

export function VerifiedCheck({ className = "", size = 16 }) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full bg-bloq-mint text-bloq-bg",
        className,
      )}
      style={{ width: size, height: size }}
      title="Verified"
      data-testid="verified-check"
    >
      <Check size={size * 0.66} strokeWidth={3.5} />
    </span>
  );
}

const BAND_STYLES = {
  "high trust": "bg-bloq-purple/20 text-bloq-purple border-bloq-purple/40",
  trusted: "text-bloq-mint border-bloq-mint/70 bg-transparent",
  elite: "bg-bloq-purple/20 text-bloq-purple border-bloq-purple/40",
};

export function TrustBand({ band, className = "" }) {
  if (!band) return null;
  const key = band.toLowerCase();
  const style = BAND_STYLES[key];
  // Baseline / New accounts show no band, matching the mobile app.
  if (!style) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
        style,
        className,
      )}
      data-testid="trust-band"
    >
      {band}
    </span>
  );
}

export function GatePill({ minScore, requireVerified, className = "" }) {
  if (!minScore && !requireVerified) return null;
  const label = `${minScore || 0}+${requireVerified ? " · Verified" : ""}`;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-bloq-purple/40 bg-bloq-purple/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-bloq-purple",
        className,
      )}
      data-testid="gate-pill"
    >
      <Shield className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

const RISK_STYLES = {
  high: "bg-destructive/15 text-destructive border-destructive/40",
  medium: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  low: "bg-bloq-mint/15 text-bloq-mint border-bloq-mint/40",
};

export function RiskPill({ risk, className = "" }) {
  if (!risk) return null;
  const style = RISK_STYLES[risk.level] || RISK_STYLES.medium;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold",
        style,
        className,
      )}
      data-testid="risk-pill"
    >
      {risk.label || risk.level}
    </span>
  );
}

// Purple "Token-gated" pill for BLOQs with an on-chain (token/NFT) requirement.
// `gate` = the token_gate object { chain, family, kind, symbol, min_amount, display }.
export function TokenGatePill({ gate, className = "" }) {
  if (!gate) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-bloq-purple/40 bg-bloq-purple/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-bloq-purple",
        className,
      )}
      title={gate.display ? `Requires ${gate.display}` : "Token-gated"}
      data-testid="token-gate-pill"
    >
      <KeyRound className="h-2.5 w-2.5" /> Token-gated
    </span>
  );
}
